import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, PencilLine, Trash, ArrowCounterClockwise, MagnifyingGlass, UploadSimple } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", default_category_id: "",
  orders_name: "", orders_phone: "", orders_email: "",
  accounts_name: "", accounts_phone: "", accounts_email: "",
  credit_terms: "", bank_name: "", account_number: "", branch_code: "" };

const CREDIT_TERMS = { "60": "60 days", "30": "30 days", "14": "14 days", "7": "7 days", cod: "Cash on Delivery" };

const fromSupplier = (s) => ({
  name: s.name, phone: s.phone || "", email: s.email || "", default_category_id: s.default_category_id || "",
  orders_name: s.orders_name || "", orders_phone: s.orders_phone || "", orders_email: s.orders_email || "",
  accounts_name: s.accounts_name || "", accounts_phone: s.accounts_phone || "", accounts_email: s.accounts_email || "",
  credit_terms: s.credit_terms || "", bank_name: s.bank_name || "",
  account_number: s.account_number || "", branch_code: s.branch_code || "",
});

export default function Suppliers() {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogStats, setCatalogStats] = useState({});
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/suppliers?all=true").then((r) => setSuppliers(r.data)).catch(() => toast.error("Failed to load suppliers"));
    api.get("/invoice-categories").then((r) => setCategories(r.data)).catch(() => {});
    api.get("/catalog-imports/stats").then((r) => {
      setCatalogStats(Object.fromEntries(r.data.map((s) => [s.supplier_id, s])));
    }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm(fromSupplier(s));
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Supplier name required");
    setBusy(true);
    try {
      const payload = { ...form, default_category_id: form.default_category_id || null };
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, payload);
        toast.success("Supplier updated");
      } else {
        await api.post("/suppliers", payload);
        toast.success("Supplier added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (s) => {
    try {
      const { data } = await api.delete(`/suppliers/${s.id}`);
      toast.success(data.deactivated ? `Deactivated — ${data.invoice_count} invoice(s) on record keep the name` : "Supplier deleted");
      load();
    } catch (e) {
      toast.error(apiError(e, "Remove failed"));
    }
  };

  const reactivate = async (s) => {
    try {
      await api.put(`/suppliers/${s.id}`, { ...fromSupplier(s), status: "active" });
      toast.success("Supplier reactivated");
      load();
    } catch (e) {
      toast.error(apiError(e, "Reactivate failed"));
    }
  };

  const filtered = suppliers.filter((s) =>
    s.name.toLowerCase().includes(search.toLowerCase()) ||
    (s.email || "").toLowerCase().includes(search.toLowerCase()) ||
    (s.phone || "").includes(search)
  );

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold">Suppliers</h1>
          <p className="text-muted-foreground mt-1">{suppliers.filter((s) => s.status !== "inactive").length} active · {suppliers.filter((s) => s.status === "inactive").length} inactive</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:w-64">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9 w-full" placeholder="Search suppliers…" data-testid="supplier-search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <Button data-testid="add-supplier-btn" onClick={openAdd} className="bg-primary hover:bg-[#8B5CF6] text-white">
            <Plus size={16} className="mr-1" /> Add supplier
          </Button>
        </div>
      </div>

      <div className="glass border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]" data-testid="suppliers-table">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Phone</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Default category</th>
              <th className="px-4 py-3 font-medium">Credit terms</th>
              <th className="px-4 py-3 font-medium">Catalog</th>
              <th className="px-4 py-3 font-medium text-right">Invoices</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className={`border-b last:border-0 ${s.status === "inactive" ? "opacity-50" : ""}`} data-testid={`supplier-row-${s.id}`}>
                <td className="px-4 py-2.5 font-medium">{s.name}</td>
                <td className="px-4 py-2.5">{s.phone || "—"}</td>
                <td className="px-4 py-2.5">{s.email || "—"}</td>
                <td className="px-4 py-2.5">{catName(s.default_category_id)}</td>
                <td className="px-4 py-2.5">{CREDIT_TERMS[s.credit_terms] || "—"}</td>
                <td className="px-4 py-2.5 text-xs" data-testid={`supplier-catalog-stats-${s.id}`}>
                  {catalogStats[s.id]?.last_import_date
                    ? <span>{catalogStats[s.id].pct_aliased ?? 0}% aliased · {catalogStats[s.id].last_import_date.slice(0, 10)}</span>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="px-4 py-2.5 num">{s.invoice_count ?? 0}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={s.status === "inactive" ? "text-muted-foreground" : "border-[#4A5D23]/40 text-[#A9C97E]"}>
                    {s.status === "inactive" ? "Inactive" : "Active"}
                  </Badge>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" data-testid={`supplier-catalog-${s.id}`} onClick={() => navigate(`/suppliers/${s.id}/catalog`)} title="Import price list / catalog">
                      <UploadSimple size={15} />
                    </Button>
                    <Button size="sm" variant="ghost" data-testid={`supplier-edit-${s.id}`} onClick={() => openEdit(s)}><PencilLine size={15} /></Button>
                    {s.status === "inactive" ? (
                      <Button size="sm" variant="ghost" data-testid={`supplier-reactivate-${s.id}`} onClick={() => reactivate(s)} title="Reactivate">
                        <ArrowCounterClockwise size={15} />
                      </Button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" data-testid={`supplier-delete-${s.id}`} className="text-destructive hover:text-destructive"><Trash size={15} /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remove {s.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              {(s.invoice_count ?? 0) > 0
                                ? `This supplier has ${s.invoice_count} invoice(s) on record, so they'll be marked inactive — removed from new invoice dropdowns, but history is preserved.`
                                : "No invoice history — this supplier will be permanently deleted."}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(s)} data-testid={`supplier-delete-confirm-${s.id}`} className="bg-destructive text-white hover:bg-destructive/90">
                              {(s.invoice_count ?? 0) > 0 ? "Deactivate" : "Delete"}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No suppliers found.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="supplier-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Supplier details form</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="supplier-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input data-testid="supplier-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" data-testid="supplier-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Default category <span className="text-muted-foreground font-normal">(pre-fills on new invoices)</span></Label>
              <Select value={form.default_category_id || "none"} onValueChange={(v) => setForm({ ...form, default_category_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="supplier-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.department}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Credit terms</Label>
              <Select value={form.credit_terms || "none"} onValueChange={(v) => setForm({ ...form, credit_terms: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="supplier-credit-terms"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not set</SelectItem>
                  {Object.entries(CREDIT_TERMS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="font-heading font-semibold text-sm mb-2">Orders contact</p>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Name" data-testid="supplier-orders-name" value={form.orders_name} onChange={(e) => setForm({ ...form, orders_name: e.target.value })} />
                <Input placeholder="Phone" data-testid="supplier-orders-phone" value={form.orders_phone} onChange={(e) => setForm({ ...form, orders_phone: e.target.value })} />
                <Input placeholder="Email" type="email" data-testid="supplier-orders-email" value={form.orders_email} onChange={(e) => setForm({ ...form, orders_email: e.target.value })} />
              </div>
            </div>
            <div>
              <p className="font-heading font-semibold text-sm mb-2">Accounts contact</p>
              <div className="grid grid-cols-3 gap-2">
                <Input placeholder="Name" data-testid="supplier-accounts-name" value={form.accounts_name} onChange={(e) => setForm({ ...form, accounts_name: e.target.value })} />
                <Input placeholder="Phone" data-testid="supplier-accounts-phone" value={form.accounts_phone} onChange={(e) => setForm({ ...form, accounts_phone: e.target.value })} />
                <Input placeholder="Email" type="email" data-testid="supplier-accounts-email" value={form.accounts_email} onChange={(e) => setForm({ ...form, accounts_email: e.target.value })} />
              </div>
            </div>
            <div>
              <p className="font-heading font-semibold text-sm mb-1">Banking <span className="text-xs font-normal text-muted-foreground">(encrypted at rest)</span></p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input placeholder="Bank" data-testid="supplier-bank-name" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
                <Input placeholder="Account number" data-testid="supplier-account-number" value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
                <Input placeholder="Branch code" data-testid="supplier-branch-code" value={form.branch_code} onChange={(e) => setForm({ ...form, branch_code: e.target.value })} />
              </div>
            </div>
            <Button onClick={submit} disabled={busy} data-testid="supplier-save-btn" className="w-full bg-primary hover:bg-[#8B5CF6] text-white">
              {busy ? "Saving…" : editing ? "Save changes" : "Add supplier"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
